import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache, split } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import { WebSocketLink } from '@apollo/client/link/ws';
import { setContext } from '@apollo/client/link/context';
import { config } from '@/config';

import { getCookie } from '@/helpers/cookies.ts';
import { clearToken } from '@/helpers/authUtils.ts';

// connection_error the backend sends for a socket without a valid session
const WS_AUTH_REQUIRED_MESSAGE = 'Authorization required';

const httpLink: ApolloLink = createHttpLink({
  uri: config.apiURI,
});

const wsLink = new WebSocketLink({
  uri: config.subscribesURI,
  options: {
    reconnect: true,
    // Open the socket with the first subscription, not on page load: the login page
    // has no session to present
    lazy: true,
    // Close it once no subscription is left (e.g. after logout, which reloads nothing):
    // the next subscription reconnects with the session current by then
    inactivityTimeout: 1000,
    // Browsers cannot set headers on a WebSocket handshake, so the session goes into the
    // connection_init payload instead; evaluated on every (re)connect
    connectionParams: () => {
      const token = getCookie('sid');
      return token ? { 'enty-auth': token } : {};
    },
    // The backend rejected the session: log out, as apolloRequests does on HTTP
    connectionCallback: (error) => {
      const message = (error as unknown as { message?: string } | undefined)?.message;
      if (message !== WS_AUTH_REQUIRED_MESSAGE || !getCookie('sid')) return;
      clearToken('sid');
      window.location.replace(`${window.location.origin}/login`);
    },
  },
});

const authLink = setContext((_, { headers }) => {
  const token = getCookie('sid');
  return {
    headers: {
      ...headers,
      ...(token ? { 'enty-auth': token } : {}),
    },
  };
});

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  authLink.concat(httpLink),
);

const cache = new InMemoryCache();
export const apolloClient = new ApolloClient({
  link: splitLink,
  cache,
});
